import { AppBar, Box, Toolbar, Typography, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import { DarkMode, LightMode } from '@mui/icons-material';
import { getTranslation } from 'common'
import { useTranslations } from 'hooks';
import FlowentiLogo from "image/Logo.svg"
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';
import { ThemeModeContext } from 'contexts';
import type { ThemeModeContextType } from 'contexts/themeModeContext';

const Navbar = ({ lang, isScrolled }: { lang?: string, isScrolled?: boolean }) => {
    const translations = useTranslations();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [eventsAnchorEl, setEventsAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate()
    const location = useLocation();
    const { mode, toggleThemeMode } = useContext<ThemeModeContextType>(ThemeModeContext);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleThemeToggle = () => {
        toggleThemeMode();
        handleClose();
    };

    const scrollToElement = (id: string) => {
        setTimeout(() => {
            const element = document.getElementById(id);
            if (element) {
                const y = element.getBoundingClientRect().top + window.scrollY - 80;
                window.scrollTo({ top: y, behavior: 'smooth' });
                // for iOS Safari which doesn't support smooth scroll
                if (!('scrollBehavior' in document.documentElement.style)) {
                    window.scrollTo(0, y);
                }
            }
        }, 150);
    };

    const scrollToSection = (id: string) => {
        handleClose();

        if (location.pathname !== '/') {
            navigate('/', { state: { scrollToSection: id } });
            return;
        }

        scrollToElement(id);
    };

    useEffect(() => {
        if (lang) {
            // console.log("Navbar lang:", lang);
        }
    }, [lang]);

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : isScrolled ? "rgba(255, 255, 255, 0.95)" : "#f5f5f7",
                transition: "0.3s",
                borderBottom: "1px solid",
                borderColor: isScrolled ? "divider" : "transparent",
                backdropFilter: isScrolled ? "blur(12px)" : "none",
                boxShadow: (theme) =>
                    isScrolled
                        ? theme.palette.mode === "dark"
                            ? "0 1px 3px rgba(0, 0, 0, 0.4)"
                            : "0 1px 3px rgba(0, 0, 0, 0.05)"
                        : "none",
            }}
        >
            <Toolbar sx={{
                display: "flex",
                justifyContent: "space-around",
                width: '100%',
                flexDirection: { xs: 'row-reverse', md: 'row' }
            }}>
                <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                    <img src={FlowentiLogo} alt="Flowenti Logo" style={{ height: 60 }} />
                    <Typography variant="h1" color="primary">Flowenti</Typography>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4, alignItems: "center", color: "text.secondary" }}>
                    <Typography fontWeight={800} sx={{
                        textDecoration: 'none',
                        color: "text.secondary",
                        cursor: 'pointer',
                        borderBottom: '2px solid transparent',
                        transition: 'border-bottom 0.2s ease-in-out',
                        '&:hover': {
                            borderBottom: '2px solid',
                            borderBottomColor: 'text.primary',
                        },
                    }} onClick={() => scrollToSection('features')}>{getTranslation(translations, 'generic.features')}</Typography>
                    <Typography fontWeight={800} sx={{
                        textDecoration: 'none',
                        color: "text.secondary",
                        cursor: 'pointer',
                        borderBottom: '2px solid transparent',
                        transition: 'border-bottom 0.2s ease-in-out',
                        '&:hover': {
                            borderBottom: '2px solid',
                            borderBottomColor: 'text.primary',
                        },
                    }} onClick={() => scrollToSection('how-it-works')}>{getTranslation(translations, 'generic.howItWorks')}</Typography>
                    {/* <Typography fontWeight={800} component={Link}
                        to="/"
                        sx={{
                            textDecoration: 'none',
                            color: "gray",
                            cursor: 'pointer',
                            borderBottom: '2px solid transparent',
                            transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid black'
                            },

                        }}>{getTranslation(translations, 'pm.propertyeditor.primaryproperties.label.documentation')}</Typography> */}
                    {/*waiting for docs on jenkins */}
                    <Typography fontWeight={800} component={Link}
                        to="/billing" sx={{
                            textDecoration: 'none',
                            color: "text.secondary",
                            cursor: 'pointer',
                            borderBottom: '2px solid transparent',
                            transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid',
                                borderBottomColor: 'text.primary',
                            },
                        }} >{getTranslation(translations, 'general_billing.plans')}</Typography>
                    <Typography fontWeight={800}
                        aria-controls={eventsAnchorEl ? 'events-menu' : undefined}
                        aria-haspopup="true"
                        onClick={(event) => setEventsAnchorEl(event.currentTarget)} sx={{
                            textDecoration: 'none',
                            color: "text.secondary",
                            cursor: 'pointer',
                            borderBottom: '2px solid transparent',
                            transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid',
                                borderBottomColor: 'text.primary',
                            },
                        }} >{getTranslation(translations, 'generic.events')}</Typography>
                    <Typography fontWeight={800} component={Link}
                        to="/roi-calculator" sx={{
                            textDecoration: 'none',
                            color: "text.secondary",
                            cursor: 'pointer',
                            borderBottom: '2px solid transparent',
                            transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid',
                                borderBottomColor: 'text.primary',
                            },
                        }} >ROI Calculator</Typography>
                    <Typography fontWeight={800} component="a"
                        href="/blog" sx={{
                            textDecoration: 'none',
                            color: "text.secondary",
                            cursor: 'pointer',
                            borderBottom: '2px solid transparent',
                            transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid',
                                borderBottomColor: 'text.primary',
                            },
                        }} >Blog</Typography>
                </Box>
                <Menu
                    id="events-menu"
                    anchorEl={eventsAnchorEl}
                    open={Boolean(eventsAnchorEl)}
                    onClose={() => setEventsAnchorEl(null)}
                >
                    <MenuItem component="a" href="/internationalization/" onClick={() => setEventsAnchorEl(null)}>
                        Toronto bpm 2026
                    </MenuItem>
                </Menu>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                    <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
                        <IconButton
                            aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                            onClick={handleThemeToggle}
                            size="small"
                            sx={{
                                color: 'primary.main',
                            }}
                        >
                            {mode === "dark" ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Typography
                        color="text.primary"
                        variant='caption'
                        fontWeight={800}
                        component={Link}
                        to="/login"
                        sx={{
                            borderBottom: '2px solid transparent',
                            textDecoration: 'none', cursor: 'pointer', mr: 2, transition: 'border-bottom 0.2s ease-in-out',
                            '&:hover': {
                                borderBottom: '2px solid',
                                borderBottomColor: 'text.primary',
                            },
                        }}
                    >
                        Login
                    </Typography>
                    <FlowentiButton
                        component={Link}
                        to="/registration"
                        size="small"
                        variant="contained"
                    >
                        {getTranslation(translations, 'generic.start_for_free')}
                    </FlowentiButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenu}
                        sx={{ color: 'primary.main' }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem onClick={handleThemeToggle}>
                            {mode === "dark" ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                        </MenuItem>
                        <MenuItem onClick={() => scrollToSection('features')}>{getTranslation(translations, 'generic.features')}</MenuItem>
                        <MenuItem onClick={() => scrollToSection('how-it-works')}>{getTranslation(translations, 'generic.howItWorks')}</MenuItem>
                        {/* <MenuItem onClick={handleClose}>{getTranslation(translations, 'pm.propertyeditor.primaryproperties.label.documentation')}</MenuItem> */}
                        <MenuItem onClick={() => navigate('/billing')}>{getTranslation(translations, 'general_billing.plans')}</MenuItem>
                        <MenuItem component={Link} to="/roi-calculator" onClick={handleClose}>ROI Calculator</MenuItem>
                        <MenuItem component="a" href="/blog" onClick={handleClose}>Blog</MenuItem>
                        <MenuItem component={Link} to="/login" onClick={handleClose}>Login</MenuItem>
                        <MenuItem component={Link} to="/registration" onClick={handleClose}>{getTranslation(translations, 'generic.start_for_free')}</MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    )
}

export default Navbar
